import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_DEPARTMENTCONTACT_LOAD_BY_ID,
    ACTION_DEPARTMENTCONTACT_LOAD_FAILURE,
    ACTION_DEPARTMENTCONTACT_LOAD_SUCCESS,

    ACTION_DEPARTMENTCONTACT_SAVE,
    ACTION_DEPARTMENTCONTACT_SAVE_SUCCESS,
    ACTION_DEPARTMENTCONTACT_SAVE_FAILURE,

    ACTION_DEPARTMENTCONTACT_CREATE,
    ACTION_DEPARTMENTCONTACT_CREATE_SUCCESS,
    ACTION_DEPARTMENTCONTACT_CREATE_FAILURE,

    ACTION_DEPARTMENTCONTACT_CLEAR,
    ACTION_DEPARTMENTCONTACT_FILTER,

    DepartmentContactAction
} from '../actions/departmentContact';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IDepartmentContactState {
    formData: Dtos.DepartmentContact | Dtos.DepartmentContact[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.Department[];
    formLength: number;
}

const initialState: IDepartmentContactState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IDepartmentContactState, _isLoading: boolean): IDepartmentContactState {
    const newState: IDepartmentContactState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: IDepartmentContactState,
    formData: Dtos.DepartmentContact | Dtos.DepartmentContact[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IDepartmentContactState {
    const newState: IDepartmentContactState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: IDepartmentContactState, responseStatus: Dtos.ResponseStatus): IDepartmentContactState {
    const newState: IDepartmentContactState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: IDepartmentContactState, formFilter: IFormFilter): IDepartmentContactState {

    const items: Dtos.DepartmentContact[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" ||
            p.name.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IDepartmentContactState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: IDepartmentContactState, propertiesToClear?: string[]): IDepartmentContactState {

    if (!propertiesToClear) {
        const newState: IDepartmentContactState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IDepartmentContactState, _isSaving: boolean): IDepartmentContactState {
    const newState: IDepartmentContactState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IDepartmentContactState,
    formData: Dtos.DepartmentContact | Dtos.DepartmentContact[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IDepartmentContactState {
    const newState: IDepartmentContactState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IDepartmentContactState, responseStatus: Dtos.ResponseStatus): IDepartmentContactState {
    const newState: IDepartmentContactState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IDepartmentContactState, _isCreating: boolean): IDepartmentContactState {
    const newState: IDepartmentContactState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IDepartmentContactState,
    formData: Dtos.DepartmentContact,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IDepartmentContactState {
    const newState: IDepartmentContactState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IDepartmentContactState, responseStatus: Dtos.ResponseStatus): IDepartmentContactState {
    const newState: IDepartmentContactState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

const DepartmentContactReducer: Reducer<IDepartmentContactState> = (state: IDepartmentContactState = initialState, action: DepartmentContactAction) => {
    switch (action.type) {
        case ACTION_DEPARTMENTCONTACT_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_DEPARTMENTCONTACT_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_DEPARTMENTCONTACT_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_DEPARTMENTCONTACT_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_DEPARTMENTCONTACT_SAVE:
            return saving(state, true);
        case ACTION_DEPARTMENTCONTACT_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_DEPARTMENTCONTACT_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_DEPARTMENTCONTACT_CREATE:
            return creating(state, true);
        case ACTION_DEPARTMENTCONTACT_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_DEPARTMENTCONTACT_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_DEPARTMENTCONTACT_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default DepartmentContactReducer;